import { format as formatDateFns, isValid, parseISO } from "date-fns";
import { computeOperationRow } from "./common";
import { EVENT_ALERT_LABEL_KEYS as KEYS } from "../constants/eventAlertLabels";

const FALLBACK_ICON = "fa-bell";

const SEVERITY_ICONS = {
  CRITICAL: "fa-circle-exclamation",
  WARNING: "fa-triangle-exclamation",
  INFO: "fa-circle-info",
};

// getLabel() returns "#locale_area_key[...]" when a key has no translation
// yet (see src/hooks/useLabels.js). Until the backend micro-patch lands,
// every Event Alerts key resolves this way - callers must not print that
// sentinel as if it were real copy, so dynamic label lookups (operation
// type names) fall back to a generic entity name instead.
const isMissingLabel = (label) => typeof label !== "string" || label.startsWith("#");

const resolveOrFallback = (getLabel, key, tokens, fallbackKey) => {
  const label = getLabel(key, tokens);
  return isMissingLabel(label) ? getLabel(fallbackKey) : label;
};

// null/undefined -> "—" (existing app convention for missing values), but a
// real 0 must survive untouched (over-eager `|| "—"` would wrongly hide it).
const orDash = (value) => (value === null || value === undefined ? "—" : value);

// Same unit_id -> label mapping the Warehouse table uses (getUnitOptions):
// ids 1,2,4,6,7 are weights (Kg), 3 is litres, 5 is plain units.
const UNIT_LABEL_KEYS = {
  1: "drugBatchUnit_Kg",
  2: "drugBatchUnit_Kg",
  3: "drugBatchUnit_L",
  4: "drugBatchUnit_Kg",
  5: "drugBatchUnit_Unit",
  6: "drugBatchUnit_Kg",
  7: "drugBatchUnit_Kg",
};

// "12" + unit_id -> "12 Kg". Without a known unit the bare value is kept.
const withUnit = (value, unitId, getLabel) => {
  if (value === null || value === undefined) return orDash(value);
  const key = UNIT_LABEL_KEYS[Number(unitId)];
  const unit = key ? getLabel(key) : null;
  return unit && !isMissingLabel(unit) ? `${value} ${unit}` : value;
};

// Raw ISO/DB dates -> the user's configured short date format; anything
// unparseable is shown as received rather than hidden.
const formatAlertDate = (value, dateFormat) => {
  if (value === null || value === undefined) return orDash(value);
  if (!dateFormat) return value;
  const date = value instanceof Date ? value : parseISO(String(value));
  return isValid(date) ? formatDateFns(date, dateFormat) : value;
};

const buildDrugStockLow = (payload, getLabel) => {
  const drugName = payload?.drug_name || getLabel(KEYS.UNKNOWN_ENTITY);
  return {
    title: getLabel(KEYS.TYPE.DRUG_STOCK_LOW.title, { drug: drugName }),
    message: getLabel(KEYS.TYPE.DRUG_STOCK_LOW.message, {
      current: withUnit(payload?.current_stock, payload?.unit_id, getLabel),
      min: withUnit(payload?.min_stock, payload?.unit_id, getLabel),
    }),
    // No drug "type" (treatment/stock) segment is present anywhere in this
    // payload (verified against the real backend normalizer in FE0), and
    // /warehouse/:type/:id needs it - fabricating it would violate "non
    // generare URL parziali o non validi", so this stays unlinked in FE1.
    deepLink: null,
  };
};

const buildDrugBatchExpiring = (payload, getLabel, { dateFormat } = {}) => {
  const drugName = payload?.drug_name || getLabel(KEYS.UNKNOWN_ENTITY);
  return {
    title: getLabel(KEYS.TYPE.DRUG_BATCH_EXPIRING.title, { drug: drugName }),
    message: getLabel(KEYS.TYPE.DRUG_BATCH_EXPIRING.message, {
      batch: orDash(payload?.batch_number),
      days: orDash(payload?.days_remaining),
      date: formatAlertDate(payload?.expire_date, dateFormat),
    }),
    deepLink: null,
  };
};

const buildDrugBatchExpired = (payload, getLabel, { dateFormat } = {}) => {
  const drugName = payload?.drug_name || getLabel(KEYS.UNKNOWN_ENTITY);
  return {
    title: getLabel(KEYS.TYPE.DRUG_BATCH_EXPIRED.title, { drug: drugName }),
    message: getLabel(KEYS.TYPE.DRUG_BATCH_EXPIRED.message, {
      batch: orDash(payload?.batch_number),
      days: orDash(payload?.days_expired),
      date: formatAlertDate(payload?.expire_date, dateFormat),
    }),
    deepLink: null,
  };
};

// Fixed list route decided by the user for FE1: no per-filter detail page
// exists in this frontend today (confirmed in FE0), so both filter alert
// types link to the same static, always-valid Warehouse "filters" list.
const FILTERS_DEEP_LINK = "/warehouse/filters";

const buildFilterCapacityWarning = (payload, getLabel) => {
  const filterName = payload?.filter_name || getLabel(KEYS.UNKNOWN_ENTITY);
  return {
    title: getLabel(KEYS.TYPE.FILTER_CAPACITY_WARNING.title, { filter: filterName }),
    message: getLabel(KEYS.TYPE.FILTER_CAPACITY_WARNING.message, {
      utilization: orDash(payload?.utilization_percent),
      threshold: orDash(payload?.threshold_percent),
    }),
    deepLink: FILTERS_DEEP_LINK,
  };
};

const buildFilterExhausted = (payload, getLabel) => {
  const filterName = payload?.filter_name || getLabel(KEYS.UNKNOWN_ENTITY);
  return {
    title: getLabel(KEYS.TYPE.FILTER_EXHAUSTED.title, { filter: filterName }),
    message: getLabel(KEYS.TYPE.FILTER_EXHAUSTED.message, {
      total: orDash(payload?.total_filtered_liters),
      capacity: orDash(payload?.max_capacity_liters),
    }),
    deepLink: FILTERS_DEEP_LINK,
  };
};

const buildOperationDeepLink = (operationType, operationId) => {
  if (!operationType || operationId === undefined || operationId === null) return null;
  return `/programming/${String(operationType).toLowerCase()}/${operationId}`;
};

// Lateness is always expressed in days (BE normalizer: unit "day").
const withDays = (value, getLabel) => {
  if (value === null || value === undefined) return orDash(value);
  const days = getLabel("days");
  return isMissingLabel(days) ? value : `${value} ${days}`;
};

const buildOperationOverdue = (payload, getLabel) => {
  // Same operation naming as the rest of the app. The alert payload carries
  // no treatments/protocol data, so ADD (whose name comes from its first
  // treatment) falls back to the plain type label.
  const operationType = payload?.operation_type;
  const operationLabel = operationType
    ? computeOperationRow(
        {
          type: operationType === "ADD" ? undefined : operationType,
          id: payload?.operation_id,
          custom_name: payload?.custom_name,
        },
        [],
        [],
        getLabel
      )
    : null;
  const operationName =
    operationType && operationType !== "ADD" && !isMissingLabel(operationLabel)
      ? operationLabel.trim()
      : operationType
        ? resolveOrFallback(getLabel, `operationType${operationType}`, undefined, KEYS.UNKNOWN_ENTITY)
        : getLabel(KEYS.UNKNOWN_ENTITY);

  return {
    title: getLabel(KEYS.TYPE.OPERATION_OVERDUE.title, { operation: operationName }),
    message: getLabel(KEYS.TYPE.OPERATION_OVERDUE.message, {
      value: withDays(payload?.lateness?.value, getLabel),
    }),
    deepLink: buildOperationDeepLink(payload?.operation_type, payload?.operation_id),
  };
};

const buildAnalysisOutOfRange = (payload, getLabel) => {
  const findings = Array.isArray(payload?.findings) ? payload.findings : [];
  const names = findings
    .map((finding) => {
      const name = finding?.substance_name;
      if (!name) return finding?.substance_id;
      // Analysis substances are translated under substance_<name>.
      const translated = getLabel(`substance_${name}`);
      return isMissingLabel(translated) ? name : translated;
    })
    .filter(Boolean);

  return {
    title: getLabel(KEYS.TYPE.ANALYSIS_OUT_OF_RANGE.title),
    message: getLabel(KEYS.TYPE.ANALYSIS_OUT_OF_RANGE.message, {
      count: findings.length,
      names: names.length > 0 ? names.join(", ") : "—",
    }),
    deepLink:
      payload?.operation_id !== undefined && payload?.operation_id !== null
        ? `/programming/analysis/${payload.operation_id}`
        : null,
  };
};

const TYPE_BUILDERS = {
  DRUG_STOCK_LOW: buildDrugStockLow,
  DRUG_BATCH_EXPIRING: buildDrugBatchExpiring,
  DRUG_BATCH_EXPIRED: buildDrugBatchExpired,
  FILTER_CAPACITY_WARNING: buildFilterCapacityWarning,
  FILTER_EXHAUSTED: buildFilterExhausted,
  OPERATION_OVERDUE: buildOperationOverdue,
  ANALYSIS_OUT_OF_RANGE: buildAnalysisOutOfRange,
};

const buildUnknownAlert = (_payload, getLabel) => ({
  title: getLabel(KEYS.UNKNOWN_TYPE.title),
  message: getLabel(KEYS.UNKNOWN_TYPE.message),
  deepLink: null,
});

// Pure, side-effect-free: no API calls, no JSX, never mutates `alert`.
const buildAlertPresentation = (alert, getLabel, options = {}) => {
  const type = alert?.type;
  const payload = alert?.payload || {};
  const severity = alert?.severity || null;
  const isKnownType = Object.prototype.hasOwnProperty.call(TYPE_BUILDERS, type);
  const builder = isKnownType ? TYPE_BUILDERS[type] : buildUnknownAlert;
  const { title, message, deepLink } = builder(payload, getLabel, options);

  return {
    title,
    message,
    severity,
    // Unknown types always get a neutral icon regardless of severity, even
    // if the record carries one - per spec ("icona neutra" for fallback).
    icon: isKnownType ? SEVERITY_ICONS[severity] || FALLBACK_ICON : FALLBACK_ICON,
    deepLink,
  };
};

export { buildAlertPresentation };
